import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend, AreaChart, Area } from "recharts";
import { toast } from "sonner";

export const StatCard = ({ label, value, tone, testId }) => {
  const colors = { green: "text-[#184A30]", red: "text-[#B04E2F]" };
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-2xl p-5" data-testid={testId}>
      <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">{label}</p>
      <p className={`serif text-4xl mt-1 ${colors[tone] || "text-[#112318]"}`}>{value}</p>
    </div>
  );
};

export function TopListCard({ title, items, keyField, testId }) {
  if (!items || items.length === 0) return (
    <div className="bg-white border border-[#E5E0D8] rounded-2xl p-5" data-testid={testId}>
      <h4 className="serif text-xl">{title}</h4>
      <p className="text-sm text-[#4A5C50] italic mt-3">None — looks clean.</p>
    </div>
  );
  const max = Math.max(...items.map((x) => x.count)) || 1;
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-2xl p-5" data-testid={testId}>
      <h4 className="serif text-xl">{title}</h4>
      <div className="mt-3 space-y-2">
        {items.map((x) => (
          <div key={x[keyField]} className="flex items-center gap-3 text-sm">
            <span className="mono text-xs flex-1 truncate">{x[keyField]}</span>
            <div className="w-32 bg-[#E5E0D8] h-2 rounded-full overflow-hidden">
              <div className="h-full bg-[#D96C4A]" style={{ width: `${(x.count / max) * 100}%` }} />
            </div>
            <span className="mono text-xs w-8 text-right text-[#B04E2F]">{x.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function LogsTab({ logs }) {
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [result, setResult] = useState("all");
  const [windowLabel, setWindowLabel] = useState("7d");

  useEffect(() => {
    api.get("/admin/login-logs-summary").then((r) => setSummary(r.data)).catch(() => setSummary(null));
  }, []);

  const cutoff = React.useMemo(() => {
    if (windowLabel === "all") return 0;
    const ms = { "24h": 86400e3, "7d": 7 * 86400e3, "30d": 30 * 86400e3 }[windowLabel];
    return Date.now() - ms;
  }, [windowLabel]);

  const filtered = logs.filter((l) => {
    const t = new Date(l.timestamp).getTime();
    if (cutoff && t < cutoff) return false;
    if (result === "success" && !l.success) return false;
    if (result === "failed" && l.success) return false;
    if (q.trim()) {
      const n = q.trim().toLowerCase();
      if (!(l.email || "").toLowerCase().includes(n) && !(l.ip || "").includes(n)) return false;
    }
    return true;
  });

  const windowStats = summary?.windows?.[windowLabel === "all" ? "30d" : windowLabel];

  const downloadCsv = async () => {
    try {
      const r = await api.post("/admin/login-logs-download-token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/login-logs.csv?token=${encodeURIComponent(r.data.token)}`;
      window.open(url, "_blank", "noopener");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to prepare download"); }
  };

  return (
    <div className="space-y-6">
      {windowStats && (
        <div className="grid md:grid-cols-5 gap-4">
          <StatCard label={`Attempts · ${windowLabel}`} value={windowStats.total} testId="logs-stat-total" />
          <StatCard label="Success" value={windowStats.success} tone="green" testId="logs-stat-success" />
          <StatCard label="Failed" value={windowStats.failed} tone="red" testId="logs-stat-failed" />
          <StatCard label="Unique emails" value={windowStats.unique_emails} testId="logs-stat-emails" />
          <StatCard label="Unique IPs" value={windowStats.unique_ips} testId="logs-stat-ips" />
        </div>
      )}

      {summary?.series_30d && (
        <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6" data-testid="logs-chart">
          <div className="flex items-center justify-between mb-4">
            <h3 className="serif text-2xl">Login activity · 30 days</h3>
            {windowStats && <p className="text-xs text-[#4A5C50] mono">success rate {windowStats.success_rate}%</p>}
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={summary.series_30d}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} stroke="#4A5C50" tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#4A5C50" />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="success" stackId="1" stroke="#184A30" fill="#184A30" fillOpacity={0.8} />
              <Area type="monotone" dataKey="failed" stackId="1" stroke="#D96C4A" fill="#D96C4A" fillOpacity={0.7} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {summary && (summary.top_failing_emails?.length > 0 || summary.top_failing_ips?.length > 0) && (
        <div className="grid md:grid-cols-2 gap-4">
          <TopListCard title="Top failing emails · 7d" items={summary.top_failing_emails} keyField="email" testId="top-fail-emails" />
          <TopListCard title="Top failing IPs · 7d" items={summary.top_failing_ips} keyField="ip" testId="top-fail-ips" />
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-center" data-testid="logs-filters">
        <input
          className="underline-input mono max-w-xs"
          placeholder="Filter by email or IP…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="logs-filter-search"
        />
        <Select value={windowLabel} onValueChange={setWindowLabel}>
          <SelectTrigger className="rounded-xl w-40" data-testid="logs-filter-window"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="24h">Last 24h</SelectItem>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="all">All time</SelectItem>
          </SelectContent>
        </Select>
        <Select value={result} onValueChange={setResult}>
          <SelectTrigger className="rounded-xl w-40" data-testid="logs-filter-result"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All results</SelectItem>
            <SelectItem value="success">Success only</SelectItem>
            <SelectItem value="failed">Failed only</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <span className="text-xs text-[#4A5C50] mono">{filtered.length} shown</span>
        <Button variant="outline" onClick={downloadCsv} className="rounded-xl border-[#184A30] text-[#184A30]" data-testid="logs-download-csv">
          Download CSV
        </Button>
      </div>

      <div className="bg-white border border-[#E5E0D8] rounded-2xl overflow-hidden">
        <table className="w-full text-sm" data-testid="logs-table">
          <thead className="text-left text-[#4A5C50] text-xs uppercase tracking-widest">
            <tr><th className="p-4">Time</th><th className="p-4">Email</th><th className="p-4">Result</th><th className="p-4">IP</th><th className="p-4">Reason</th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-sm text-[#4A5C50] italic">No login attempts match these filters.</td></tr>
            )}
            {filtered.map((l) => {
              const isRegister = l.reason === "register";
              return (
                <tr
                  key={l.id}
                  className={`border-t border-[#E5E0D8] ${isRegister ? "bg-[#CBA35C]/8" : ""}`}
                  data-testid={isRegister ? `signup-row-${l.id}` : undefined}
                >
                  <td className="p-4 mono text-xs">{new Date(l.timestamp).toLocaleString()}</td>
                  <td className="p-4 mono text-xs">{l.email}</td>
                  <td className="p-4">
                    {isRegister ? (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#CBA35C]/20 text-[#8C6B21] font-semibold">
                        ✦ New sign-up
                      </span>
                    ) : (
                      <span className={`text-[11px] uppercase tracking-widest px-2 py-1 rounded-full ${l.success ? "bg-[#184A30]/10 text-[#184A30]" : "bg-[#D96C4A]/15 text-[#B04E2F]"}`}>
                        {l.success ? "Success" : "Failed"}
                      </span>
                    )}
                  </td>
                  <td className="p-4 mono text-xs">{l.ip}</td>
                  <td className="p-4 text-[#4A5C50] text-xs">{l.reason}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
